import { useCallback, useEffect, useRef, useState } from "react";
import { compassHeadingFromEuler, normalizeDeg, smoothAngle } from "../lib/geo";

export type HeadingStatus =
  | "unsupported" // Tarayıcı/cihaz yönelim olayı vermiyor
  | "needs-permission" // iOS: kullanıcı hareketiyle izin istenmeli
  | "denied"
  | "waiting" // İzin var, ilk veri bekleniyor
  | "ready";

export interface HeadingState {
  /** Ekranın üst kenarının baktığı yön (0 = kuzey). */
  heading: number | null;
  status: HeadingStatus;
  /** iOS'ta bildirilen pusula hata payı (derece); yoksa null. */
  accuracy: number | null;
  /** Sensör kalibrasyon istiyor. */
  needsCalibration: boolean;
  /** Manyetik değil gerçek kuzeye göre veri geliyor (iOS). */
  isTrueNorth: boolean;
  requestPermission: () => Promise<void>;
}

interface WebkitDeviceOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

type PermissionCapableEvent = {
  requestPermission?: () => Promise<"granted" | "denied" | "default">;
};

const SMOOTHING = 0.25;

function screenAngle(): number {
  if (typeof window === "undefined") return 0;
  const angle = window.screen?.orientation?.angle;
  if (typeof angle === "number") return angle;
  const legacy = (window as unknown as { orientation?: number }).orientation;
  return typeof legacy === "number" ? legacy : 0;
}

function needsIosPermission(): boolean {
  if (typeof DeviceOrientationEvent === "undefined") return false;
  return typeof (DeviceOrientationEvent as unknown as PermissionCapableEvent)
    .requestPermission === "function";
}

export function useHeading(): HeadingState {
  const [heading, setHeading] = useState<number | null>(null);
  const [status, setStatus] = useState<HeadingStatus>("waiting");
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [isTrueNorth, setIsTrueNorth] = useState(false);
  const [granted, setGranted] = useState(!needsIosPermission());
  const smoothedRef = useRef<number>(Number.NaN);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof DeviceOrientationEvent === "undefined") {
      setStatus("unsupported");
      return;
    }
    if (!granted) {
      setStatus("needs-permission");
      return;
    }

    setStatus((current) => (current === "ready" ? current : "waiting"));

    let gotAbsolute = false;

    const handle = (event: DeviceOrientationEvent) => {
      const webkitEvent = event as WebkitDeviceOrientationEvent;
      let raw: number | null = null;

      if (typeof webkitEvent.webkitCompassHeading === "number") {
        // iOS: doğrudan gerçek kuzeye göre pusula açısı.
        raw = webkitEvent.webkitCompassHeading;
        setIsTrueNorth(true);
        const acc = webkitEvent.webkitCompassAccuracy;
        setAccuracy(typeof acc === "number" && acc >= 0 ? acc : null);
      } else if (
        event.absolute &&
        event.alpha !== null &&
        event.beta !== null &&
        event.gamma !== null
      ) {
        raw = compassHeadingFromEuler(event.alpha, event.beta, event.gamma);
        setIsTrueNorth(false);
        setAccuracy(null);
      }

      if (raw === null) return;

      gotAbsolute = true;
      const corrected = normalizeDeg(raw + screenAngle());
      smoothedRef.current = smoothAngle(smoothedRef.current, corrected, SMOOTHING);
      setHeading(smoothedRef.current);
      setStatus("ready");
    };

    // Android/Chrome mutlak yönelimi ayrı olayla verir; iOS klasik olayı kullanır.
    window.addEventListener("deviceorientationabsolute", handle as EventListener);
    window.addEventListener("deviceorientation", handle);

    const timeout = window.setTimeout(() => {
      if (!gotAbsolute) setStatus("unsupported");
    }, 3000);

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener(
        "deviceorientationabsolute",
        handle as EventListener,
      );
      window.removeEventListener("deviceorientation", handle);
    };
  }, [granted]);

  const requestPermission = useCallback(async () => {
    const target = DeviceOrientationEvent as unknown as PermissionCapableEvent;
    if (typeof target.requestPermission !== "function") {
      setGranted(true);
      return;
    }
    try {
      const result = await target.requestPermission();
      if (result === "granted") {
        setGranted(true);
      } else {
        setStatus("denied");
      }
    } catch {
      setStatus("denied");
    }
  }, []);

  const needsCalibration =
    status === "ready" && accuracy !== null && accuracy > 15;

  return {
    heading,
    status,
    accuracy,
    needsCalibration,
    isTrueNorth,
    requestPermission,
  };
}
